import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { formatCurrency } from "../utils/formatters";

/**
 * Safe date formatter - prevents RangeError: Invalid time value
 */
function safeDateFormat(value, formatStr = "MMM d, yyyy") {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return "—";
    return format(date, formatStr);
  } catch {
    return "—";
  }
}

/**
 * Safe date comparison - returns false for invalid dates
 */
function isDatePast(value) {
  if (!value) return false;
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return false;
    return date < new Date();
  } catch {
    return false;
  }
}
import {
  Receipt,
  DotsThree,
  PaperPlaneTilt,
  CurrencyDollar,
  Eye,
  Warning,
} from "phosphor-react";

// Import modular invoice components
import {
  InvoiceCreateModal,
  InvoiceDetailModal,
  InvoiceEditModal,
  InvoiceDeleteDialog,
  InvoicePaymentModal,
} from "../components/invoices";
import BottomQuickActions from "../components/BottomQuickActions";
import { useLiveFilter, getInvoiceSearchText } from "../hooks/useLiveFilter";
import { useSearchFilter } from "../layouts/AppLayout";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Custom status order for sorting
const STATUS_ORDER = {
  draft: 0,
  partial: 1,
  sent: 2,
  paid: 3,
  overdue: 4,
  canceled: 5,
};

export default function Invoices() {
  // Data state
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taxRatePercent, setTaxRatePercent] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");

  // URL params for auto-opening create modal
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Use shared filter from layout header
  const { invoicesFilter: filterQuery } = useSearchFilter();

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    customer_id: "",
    title: "",
    items: [{ description: "", quantity: "", price: "", is_taxable: false }],
    notes: "",
    payment_terms: "due_on_receipt",
  });
  const [editFormData, setEditFormData] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("check");
  const [lineItemErrors, setLineItemErrors] = useState([]); // Validation errors

  // Memoized search text extractor for invoices
  const invoiceSearchText = useCallback(getInvoiceSearchText, []);

  // Apply status filter first
  const statusFilteredInvoices = useMemo(() => {
    if (statusFilter === "all") return invoices;
    return invoices.filter(i => i.status === statusFilter);
  }, [invoices, statusFilter]);

  // Live filter by text query
  const filteredInvoices = useLiveFilter(statusFilteredInvoices, filterQuery, invoiceSearchText);

  // Sort filtered invoices: status order + oldest to newest by date
  const displayedInvoices = useMemo(() => {
    return [...filteredInvoices].sort((a, b) => {
      const statusDiff = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
      if (statusDiff !== 0) return statusDiff;
      
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateA - dateB;
    });
  }, [filteredInvoices]);

  // ========================
  // DATA FETCHING
  // ========================

  useEffect(() => {
    fetchInvoices();
    fetchCustomers();
    fetchTaxSettings();
  }, []);
  
  // Handle ?new=1 query param to auto-open create modal
  useEffect(() => {
    const shouldOpenNew = searchParams.get("new") === "1";
    if (shouldOpenNew && !isCreateModalOpen) {
      setIsCreateModalOpen(true);
      // Remove the param to prevent re-opening on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [searchParams, isCreateModalOpen, navigate, location.pathname]);

  const fetchInvoices = async () => {
    try {
      const response = await fetch(`${API}/invoices`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API}/customers`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchTaxSettings = async () => {
    try {
      const response = await fetch(`${API}/estimates/tax-settings`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setTaxRatePercent(data.tax_rate_percent || 0);
      }
    } catch (error) {
      console.error("Error fetching tax settings:", error);
    }
  };

  // ========================
  // CREATE INVOICE
  // ========================

  const resetForm = () => {
    setFormData({
      customer_id: "",
      title: "",
      items: [{ description: "", quantity: "", price: "", is_taxable: false }],
      notes: "",
      payment_terms: "due_on_receipt",
    });
    setLineItemErrors([]);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    
    // Import validation and normalization functions
    const { validateLineItems, normalizeLineItems } = await import("../components/LineItemsEditor");
    const { normalizeApiError } = await import("../utils/apiErrorNormalizer");
    
    // Normalize line items first (auto-fill qty=1 if price set but qty empty)
    const normalizedItems = normalizeLineItems(formData.items);
    const normalizedFormData = { ...formData, items: normalizedItems };
    
    // Client-side validation
    const errors = validateLineItems(normalizedItems);
    if (errors.length > 0) {
      setLineItemErrors(errors);
      toast.error("Please fix the errors in line items before saving.");
      return;
    }
    setLineItemErrors([]);
    
    try {
      const response = await fetch(`${API}/invoices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(normalizedFormData),
      });

      if (response.ok) {
        toast.success("Invoice created");
        setIsCreateModalOpen(false);
        resetForm();
        fetchInvoices();
      } else {
        const data = await response.json();
        const friendlyError = normalizeApiError({ response: { status: response.status, data } });
        toast.error(friendlyError);
      }
    } catch (error) {
      const { normalizeApiError } = await import("../utils/apiErrorNormalizer");
      toast.error(normalizeApiError(error));
    }
  };

  // Line item handlers for create form
  const addLineItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", quantity: "", price: "", is_taxable: false }],
    });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...formData.items];
    if (field === "quantity") {
      // Allow empty, store as string for input control
      newItems[index][field] = value;
    } else if (field === "is_taxable") {
      newItems[index][field] = value;
    } else {
      newItems[index][field] = value;
    }
    setFormData({ ...formData, items: newItems });
  };

  const formatPrice = (index) => {
    const newItems = [...formData.items];
    const val = parseFloat(newItems[index].price);
    if (!isNaN(val) && val > 0) {
      newItems[index].price = val.toFixed(2);
      // AUTO-FILL: If price is set and qty is empty, set qty to 1
      if (newItems[index].quantity === "" || newItems[index].quantity === null || newItems[index].quantity === undefined) {
        newItems[index].quantity = "1";
      }
    } else if (newItems[index].price === "" || newItems[index].price === "0") {
      newItems[index].price = "";
    }
    setFormData({ ...formData, items: newItems });
  };

  const handleCatalogSelect = (index, catalogItem) => {
    const newItems = [...formData.items];
    const currentQuantity = newItems[index].quantity;
    // Preserve existing quantity if user has set one, otherwise keep empty
    newItems[index] = {
      ...newItems[index],
      description: catalogItem.description,
      price: catalogItem.price.toFixed(2),
      is_taxable: catalogItem.is_taxable,
      quantity: currentQuantity !== "" ? currentQuantity : "",
      catalog_item_id: catalogItem.catalog_item_id,
    };
    setFormData({ ...formData, items: newItems });
  };

  const removeLineItem = (index) => {
    if (formData.items.length > 1) {
      setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
    }
  };

  // ========================
  // VIEW INVOICE
  // ========================

  const viewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setIsDetailModalOpen(true);
  };

  // ========================
  // INVOICE ACTIONS
  // ========================

  const sendInvoice = async (invoiceId) => {
    try {
      const response = await fetch(`${API}/invoices/${invoiceId}/send`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        const sendResult = await response.json();
        toast.success("Invoice sent to customer");
        fetchInvoices();
        
        // If modal is open, update selectedInvoice with new status but preserve all existing fields
        // The send response only returns {success, message, public_url, status} - NOT full invoice
        if (isDetailModalOpen && selectedInvoice && selectedInvoice.invoice_id === invoiceId) {
          setSelectedInvoice({
            ...selectedInvoice,  // Preserve all existing invoice data (items, totals, etc.)
            status: sendResult.status || "sent",
            sent_at: new Date().toISOString(),
          });
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send invoice");
      }
    } catch (error) {
      toast.error("Failed to send invoice");
    }
  };

  // ========================
  // EDIT INVOICE
  // ========================

  const openEditModal = () => {
    if (!selectedInvoice) return;
    const currentPaymentTerms = selectedInvoice.payment_terms || "due_on_receipt";
    setEditFormData({
      invoice_id: selectedInvoice.invoice_id,
      customer_id: selectedInvoice.customer_id,
      customer_name: selectedInvoice.customer?.name || "",
      title: selectedInvoice.title,
      items: selectedInvoice.items.map(item => ({
        description: item.description,
        quantity: item.quantity != null ? String(item.quantity) : "",
        price: item.price?.toFixed(2) || "",
        is_taxable: item.is_taxable || false,
      })),
      notes: selectedInvoice.notes || "",
      payment_terms: currentPaymentTerms,
      _original_payment_terms: currentPaymentTerms,  // Track original for due_date display logic
      issue_date: selectedInvoice.issue_date,
      due_date: selectedInvoice.due_date,
      created_at: selectedInvoice.created_at,
    });
    setIsEditModalOpen(true);
  };

  const updateEditLineItem = (index, field, value) => {
    const newItems = [...editFormData.items];
    if (field === "quantity") {
      // Store as string to allow natural input editing (backspace, delete, cursor)
      newItems[index][field] = value;
    } else if (field === "is_taxable") {
      newItems[index][field] = value;
    } else {
      newItems[index][field] = value;
    }
    setEditFormData({ ...editFormData, items: newItems });
  };

  const formatEditPrice = (index) => {
    const newItems = [...editFormData.items];
    const val = parseFloat(newItems[index].price);
    if (!isNaN(val) && val > 0) {
      newItems[index].price = val.toFixed(2);
      // AUTO-FILL: If price is set and qty is empty, set qty to 1
      if (newItems[index].quantity === "" || newItems[index].quantity === null || newItems[index].quantity === undefined) {
        newItems[index].quantity = "1";
      }
    } else if (newItems[index].price === "" || newItems[index].price === "0") {
      newItems[index].price = "";
    }
    setEditFormData({ ...editFormData, items: newItems });
  };

  const addEditLineItem = () => {
    setEditFormData({
      ...editFormData,
      items: [...editFormData.items, { description: "", quantity: "", price: "", is_taxable: false }],
    });
  };

  const removeEditLineItem = (index) => {
    if (editFormData.items.length > 1) {
      setEditFormData({ ...editFormData, items: editFormData.items.filter((_, i) => i !== index) });
    }
  };

  const handleEditCatalogSelect = (index, catalogItem) => {
    const newItems = [...editFormData.items];
    const currentQuantity = newItems[index].quantity;
    // Preserve existing quantity if user has set one, otherwise keep empty
    newItems[index] = {
      ...newItems[index],
      description: catalogItem.description,
      price: catalogItem.price.toFixed(2),
      is_taxable: catalogItem.is_taxable,
      quantity: currentQuantity !== "" ? currentQuantity : "",
    };
    setEditFormData({ ...editFormData, items: newItems });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    // Import normalization function and normalize items before submit
    const { normalizeLineItems } = await import("../components/LineItemsEditor");
    const normalizedItems = normalizeLineItems(editFormData.items);
    
    try {
      const response = await fetch(`${API}/invoices/${editFormData.invoice_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: editFormData.title,
          items: normalizedItems,
          notes: editFormData.notes,
          payment_terms: editFormData.payment_terms,
        }),
      });

      if (response.ok) {
        const updatedInvoice = await response.json();
        toast.success("Invoice updated");
        setIsEditModalOpen(false);
        setSelectedInvoice(updatedInvoice);
        fetchInvoices();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update invoice");
      }
    } catch (error) {
      toast.error("Failed to update invoice");
    }
  };

  // ========================
  // DELETE INVOICE
  // ========================

  const openDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteInvoice = async () => {
    if (!selectedInvoice) return;
    try {
      const response = await fetch(`${API}/invoices/${selectedInvoice.invoice_id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Invoice deleted");
        setIsDeleteDialogOpen(false);
        setIsDetailModalOpen(false);
        fetchInvoices();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete invoice");
      }
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  // ========================
  // PAYMENT
  // ========================

  const openPaymentModal = async (invoice) => {
    setSelectedInvoice(invoice);
    // Compute balance_due if missing (legacy invoices)
    const balanceDue = invoice.balance_due ?? (invoice.total - (invoice.amount_paid || 0));
    setPaymentAmount(balanceDue > 0 ? balanceDue.toFixed(2) : "");
    
    // Fetch customer's saved preferred payment method from backend
    const customerId = invoice.customer_id || invoice.customer?.customer_id;
    let preferredMethod = "check"; // Default
    
    if (customerId) {
      try {
        const customerRes = await fetch(`${API}/customers/${customerId}`, { credentials: "include" });
        if (customerRes.ok) {
          const customerData = await customerRes.json();
          if (customerData.preferred_payment_method) {
            preferredMethod = customerData.preferred_payment_method;
          }
        }
      } catch (error) {
        console.error("Error fetching customer payment preference:", error);
      }
    }
    
    setPaymentMethod(preferredMethod);
    setIsPaymentModalOpen(true);
  };

  const recordPayment = async () => {
    if (!selectedInvoice || !paymentAmount) return;
    try {
      const response = await fetch(`${API}/invoices/${selectedInvoice.invoice_id}/record-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          amount: parseFloat(paymentAmount),
          method: paymentMethod,
        }),
      });
      if (response.ok) {
        toast.success("Payment recorded");
        
        // Save this customer's payment method preference to backend
        const customerId = selectedInvoice.customer_id || selectedInvoice.customer?.customer_id;
        if (customerId) {
          try {
            await fetch(`${API}/customers/${customerId}/payment-preference`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ preferred_payment_method: paymentMethod }),
            });
          } catch (error) {
            console.error("Error saving payment preference:", error);
          }
        }
        
        setIsPaymentModalOpen(false);
        setPaymentAmount("");
        setPaymentMethod("check");
        fetchInvoices();
        const updated = await response.json();
        setSelectedInvoice(updated);
      }
    } catch (error) {
      toast.error("Failed to record payment");
    }
  };

  // ========================
  // HELPERS
  // ========================

  const getStatusColor = (status) => {
    const colors = {
      draft: "bg-zinc-100 text-zinc-700 border-zinc-300",
      sent: "bg-blue-50 text-blue-700 border-blue-300",
      partial: "bg-amber-50 text-amber-700 border-amber-300",
      paid: "bg-emerald-50 text-emerald-700 border-emerald-300",
      overdue: "bg-red-50 text-red-700 border-red-300",
    };
    return colors[status] || colors.draft;
  };

  // ========================
  // RENDER
  // ========================

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Controls Row - Status dropdown only (filter is in layout header) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] h-10 border-2" data-testid="status-filter-select">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Invoices</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>

        {/* Create Modal */}
        <InvoiceCreateModal
          isOpen={isCreateModalOpen}
          onOpenChange={setIsCreateModalOpen}
          formData={formData}
          setFormData={setFormData}
          customers={customers}
          taxRatePercent={taxRatePercent}
          onSubmit={handleCreateSubmit}
          onUpdateLineItem={updateLineItem}
          onAddLineItem={addLineItem}
          onRemoveLineItem={removeLineItem}
          onCatalogSelect={handleCatalogSelect}
          onFormatPrice={formatPrice}
          onReset={resetForm}
          onCustomerCreated={(customer) => {
            setCustomers((prev) => [customer, ...prev]);
          }}
          validationErrors={lineItemErrors}
        />
      </div>

      {/* Invoices List */}
      {loading ? (
        <div className="text-center py-12 text-zinc-500">Loading invoices...</div>
      ) : displayedInvoices.length === 0 && invoices.length === 0 ? (
        <Card className="border-2 border-dashed">
          <CardContent className="py-12 text-center">
            <Receipt size={48} className="mx-auto mb-4 text-zinc-300" />
            <h3 className="font-heading text-lg uppercase mb-2">No invoices yet</h3>
            <p className="text-zinc-500 mb-4">Create your first invoice to start getting paid</p>
          </CardContent>
        </Card>
      ) : displayedInvoices.length === 0 && (filterQuery.trim() || statusFilter !== "all") ? (
        <Card className="border-2 border-dashed border-zinc-300">
          <CardContent className="py-12 text-center">
            <Receipt size={48} className="mx-auto mb-4 text-zinc-300" />
            <h3 className="font-heading text-lg uppercase mb-2">No Matching Invoices</h3>
            <p className="text-zinc-500">
              No invoices match your filter
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {/* Show filter indicator when filtering */}
          {filterQuery.trim() && (
            <div className="text-sm text-zinc-500 bg-zinc-100 px-3 py-2 rounded-lg" data-testid="filter-results-count">
              Showing {displayedInvoices.length} of {statusFilteredInvoices.length} invoices
            </div>
          )}
          {displayedInvoices.map((invoice) => (
            <Card
              key={invoice.invoice_id}
              className="border-2 hover:border-[#FF6600]/50 transition-colors cursor-pointer"
              onClick={() => viewInvoice(invoice)}
              data-testid={`invoice-card-${invoice.invoice_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-sm text-zinc-500 font-mono">{invoice.invoice_number}</span>
                      <Badge variant="outline" className={getStatusColor(invoice.status)}>
                        {invoice.status}
                      </Badge>
                      {invoice.due_date && isDatePast(invoice.due_date) && invoice.status !== "paid" && (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
                          <Warning size={12} className="mr-1" /> Overdue
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-medium">{invoice.title}</h3>
                    <p className="text-sm text-zinc-500">{invoice.customer?.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-xl font-bold">
                      {formatCurrency(invoice.balance_due)}
                      <span className="text-xs text-zinc-400 font-normal ml-1">due</span>
                    </p>
                    <p className="text-xs text-zinc-400">
                      of {formatCurrency(invoice.total)}
                    </p>
                    {invoice.due_date && (
                      <p className="text-xs text-zinc-400 mt-1">
                        Due: {safeDateFormat(invoice.due_date, "MMM d")}
                      </p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="ml-4">
                        <DotsThree size={20} weight="bold" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); viewInvoice(invoice); }}>
                        <Eye size={16} className="mr-2" /> View Details
                      </DropdownMenuItem>
                      {(invoice.status === "draft" || invoice.status === "sent" || invoice.sent_at || invoice.revision_pending) && invoice.status !== "paid" && invoice.status !== "void" && (
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); sendInvoice(invoice.invoice_id); }}>
                          <PaperPlaneTilt size={16} className="mr-2" /> 
                          {invoice.status === "draft" ? "Send Invoice" : "Resend Invoice"}
                        </DropdownMenuItem>
                      )}
                      {invoice.status !== "paid" && invoice.status !== "draft" && (invoice.balance_due > 0 || invoice.balance_due === undefined) && (
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openPaymentModal(invoice); }}>
                          <CurrencyDollar size={16} className="mr-2" /> Record Payment
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <InvoiceDetailModal
        isOpen={isDetailModalOpen}
        onOpenChange={setIsDetailModalOpen}
        invoice={selectedInvoice}
        onSend={sendInvoice}
        onRecordPayment={openPaymentModal}
        onEdit={openEditModal}
        onDelete={openDeleteDialog}
        getStatusColor={getStatusColor}
      />

      {/* Edit Modal */}
      <InvoiceEditModal
        isOpen={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        editFormData={editFormData}
        setEditFormData={setEditFormData}
        taxRatePercent={taxRatePercent}
        onSubmit={handleEditSubmit}
        onUpdateLineItem={updateEditLineItem}
        onAddLineItem={addEditLineItem}
        onRemoveLineItem={removeEditLineItem}
        onCatalogSelect={handleEditCatalogSelect}
        onFormatPrice={formatEditPrice}
      />

      {/* Delete Dialog */}
      <InvoiceDeleteDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteInvoice}
      />

      {/* Payment Modal */}
      <InvoicePaymentModal
        isOpen={isPaymentModalOpen}
        onOpenChange={setIsPaymentModalOpen}
        invoice={selectedInvoice}
        paymentAmount={paymentAmount}
        setPaymentAmount={setPaymentAmount}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        onSubmit={recordPayment}
      />

      {/* Quick Actions */}
      <BottomQuickActions />
    </div>
  );
}
